/**
 * Format de stockage canonique des téléphones dans Doorly : **E.164 français
 * mobile** (`+33[67]XXXXXXXX`). Tous les inputs (UI, API, partenaires)
 * sont normalisés vers ce format à la création / mise à jour ; les outputs
 * (DB, mappings outbound) servent ce même format. L'affichage humain se
 * fait via `formatFrenchPhone` qui re-convertit en `XX XX XX XX XX`.
 *
 * Seul le mobile FR (06/07) est validé strictement. Les numéros legacy
 * non-FR ou non-mobiles peuvent transiter via la base (data importée),
 * `toE164French` ne les transforme pas et `isValidE164FrenchMobile`
 * retourne `false` sur eux.
 */

/** Strip everything that isn't a digit or `+`. */
function stripPhoneRaw(input: string): string {
  return input.replace(/[^\d+]/g, '');
}

/**
 * Normalise un téléphone vers `+33[67]XXXXXXXX` quand l'input correspond
 * à un mobile FR (06/07 local, +33 international, avec ou sans espaces).
 * Les autres formats (numéros internationaux non-FR, fixes, etc.) sont
 * retournés tels quels (après strip) — décision PO de ne pas casser la
 * data legacy.
 */
export function toE164French(input: string | null | undefined): string {
  if (input == null) return '';
  const cleaned = stripPhoneRaw(String(input));
  // Déjà en `+33[67]XXXXXXXX` → on retourne tel quel.
  if (/^\+33[67]\d{8}$/.test(cleaned)) return cleaned;
  // Local FR `0[67]XXXXXXXX` (10 chiffres) → ajoute le préfixe.
  if (/^0[67]\d{8}$/.test(cleaned)) return `+33${cleaned.slice(1)}`;
  // `33[67]XXXXXXXX` sans `+` (saisie partielle) → ajoute juste le `+`.
  if (/^33[67]\d{8}$/.test(cleaned)) return `+${cleaned}`;
  // `00 33[67]XXXXXXXX` (notation européenne) → réécrit en `+33`.
  if (/^0033[67]\d{8}$/.test(cleaned)) return `+${cleaned.slice(2)}`;
  // Tout autre format laissé tel quel — la validation suit côté schema.
  return cleaned;
}

/** Regex de validation E.164 mobile FR. */
export const E164_FRENCH_MOBILE_REGEX = /^\+33[67]\d{8}$/;

/** Vrai uniquement quand l'input est exactement `+33[67]XXXXXXXX`. */
export function isValidE164FrenchMobile(input: string): boolean {
  return E164_FRENCH_MOBILE_REGEX.test(input);
}

/**
 * Inverse de `toE164French` : `+33623070207` → `0623070207`. Sert au
 * formatage display (ré-applique ensuite "XX XX XX XX XX"). Accepte tout
 * numéro FR à 9 chiffres après `+33`, fixe compris : seul le mobile est
 * VALIDÉ, mais les numéros miroirs des call-centers sont des fixes
 * (`+334…`) et doivent s'afficher `04 …` comme les autres. Pour les inputs
 * déjà locaux ou non-FR, retourne l'input tel quel.
 */
export function fromE164ToLocalFrench(input: string | null | undefined): string {
  if (input == null) return '';
  const s = String(input);
  if (/^\+33[1-9]\d{8}$/.test(s)) return `0${s.slice(3)}`;
  return s;
}

// ── Recherche par téléphone ─────────────────────────────────────────────
//
// Les numéros sont stockés en E.164 (`+33623070207`) mais tapés de toutes
// les façons : `0623070207`, `06 23 07 02 07`, `+33 6 23…`, `0033…`, ou un
// fragment (`06 23`). On compare des CHIFFRES, sous les deux formes FR :
// internationale (`33623070207`) et nationale (`0623070207`).

/** Formes chiffrées d'un numéro stocké, pour la recherche. */
export function phoneSearchForms(phone: string | null | undefined): string[] {
  if (!phone) return [];
  const digits = String(phone).replace(/\D/g, '');
  if (!digits) return [];
  const forms = [digits];
  if (/^\+33/.test(String(phone)) || /^33\d{9}$/.test(digits)) forms.push(`0${digits.slice(2)}`);
  return forms;
}

/**
 * Chiffres à chercher si la saisie ressemble à un numéro (chiffres, espaces,
 * `+`, `.`, `-`, parenthèses ; au moins 3 chiffres), sinon `null` — la
 * recherche texte habituelle s'applique alors.
 */
export function phoneSearchQuery(query: string | null | undefined): string[] | null {
  const q = (query ?? '').trim();
  if (!/^[\d\s+().-]+$/.test(q)) return null;
  const digits = q.replace(/\D/g, '');
  if (digits.length < 3) return null;
  const forms = [digits];
  // `0033…` (notation européenne) → `33…` et `0…`.
  if (digits.startsWith('0033')) forms.push(digits.slice(2), `0${digits.slice(4)}`);
  // `+33…` → aussi sous forme nationale `0…`.
  else if (q.startsWith('+') && digits.startsWith('33')) forms.push(`0${digits.slice(2)}`);
  return forms;
}

/** Le numéro stocké correspond-il à la saisie (formes comparées en chiffres) ? */
export function phoneMatchesSearch(
  phone: string | null | undefined,
  queryForms: readonly string[],
): boolean {
  const forms = phoneSearchForms(phone);
  return queryForms.some((q) => forms.some((f) => f.includes(q)));
}
